if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.prepareDeferredImages();
      }

      disconnectedCallback() {
        this.imageObserver?.disconnect();
        this.deferredImageGeneration = (this.deferredImageGeneration || 0) + 1;
      }

      hydrateGalleryImage(item, loading = 'eager', priority = 'auto') {
        if (!item) return;
        item.querySelectorAll('template[data-cf-gallery-image]').forEach((template) => {
          const content = template.content.cloneNode(true);
          content.querySelectorAll('img').forEach((image) => {
            image.dataset.cfGalleryHydrated = 'true';
            if (loading === 'eager') image.sizes = image.sizes.replace(/^auto,\s*/, '');
            image.loading = loading;
            image.fetchPriority = priority;
          });
          template.replaceWith(content);
        });
        if (loading === 'eager') {
          item.querySelectorAll('img[data-cf-gallery-hydrated]').forEach((image) => {
            image.sizes = image.sizes.replace(/^auto,\s*/, '');
            image.loading = 'eager';
            image.fetchPriority = priority;
          });
        }
        this.imageObserver?.unobserve(item);
      }

      prepareDeferredImages() {
        this.imageObserver?.disconnect();
        if (!this.querySelector("template[data-cf-gallery-image]")) return;
        const generation = (this.deferredImageGeneration || 0) + 1;
        this.deferredImageGeneration = generation;
        const active = this.querySelector(".product__media-item.is-active");
        this.hydrateGalleryImage(active);
        const firstImage = active?.querySelector("img");
        const hydrateNearby = () => {
          if (!this.isConnected || generation !== this.deferredImageGeneration) return;
          const templates = this.querySelectorAll("template[data-cf-gallery-image]");
          if ("IntersectionObserver" in window) {
            this.imageObserver = new IntersectionObserver((entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) this.hydrateGalleryImage(entry.target, "lazy", "low");
              });
            }, { rootMargin: "150px 0px" });
            templates.forEach((template) => {
              const item = template.closest(".product__media-item");
              if (item) this.imageObserver.observe(item);
              else this.hydrateGalleryImage(template.parentElement, "lazy", "low");
            });
          } else {
            templates.forEach((template) => this.hydrateGalleryImage(template.parentElement, "lazy", "low"));
          }
          this.hydrateGalleryImage(active?.nextElementSibling, "lazy", "low");
        };
        const afterPrimary = () => {
          // Give the primary image a paint opportunity before preparing nearby slides.
          requestAnimationFrame(() => requestAnimationFrame(() => {
            if ("requestIdleCallback" in window) window.requestIdleCallback(hydrateNearby, { timeout: 1000 });
            else window.setTimeout(hydrateNearby, 0);
          }));
        };
        if (!firstImage || firstImage.complete) afterPrimary();
        else {
          firstImage.addEventListener("load", afterPrimary, { once: true });
          firstImage.addEventListener("error", afterPrimary, { once: true });
        }
      }

      onSlideChanged(event) {
        this.hydrateGalleryImage(event.detail.currentElement);
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        this.hydrateGalleryImage(activeMedia);
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(".product__modal-opener--image img");
        if (!image || !this.elements.liveRegion) return;
        const version = (this.galleryAnnouncementVersion || 0) + 1;
        this.galleryAnnouncementVersion = version;
        const announce = () => {
          if (!this.isConnected || version !== this.galleryAnnouncementVersion || !image.naturalWidth) return;
          window.clearTimeout(this.galleryAnnouncementTimer);
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace("[index]", position);
          this.galleryAnnouncementTimer = window.setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        if (image.complete) announce();
        else image.addEventListener("load", announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

